import React, { useEffect, useRef, useState, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import { toast } from "sonner";
import { Send, Lock, ShieldCheck, Reply, Flag, Pin, Trash2, X, Users, AlertTriangle, Pencil, Bell, SmilePlus } from "lucide-react";
import { api } from "@/lib/api";

const REACTIONS = ["👍", "❤️", "🔥", "🙏"];
const MORE_EMOJIS = ["👍", "❤️", "🔥", "🙏", "😂", "😮", "😢", "👏", "🎉", "💪", "💰", "✅", "🤔", "😊", "🚀", "👌"];

function timeLabel(iso) {
  try {
    const d = new Date(iso);
    return d.toLocaleString("id-ID", { day: "numeric", month: "short", hour: "2-digit", minute: "2-digit" });
  } catch {
    return "";
  }
}

export default function Community() {
  const navigate = useNavigate();
  const [access, setAccess] = useState(null);
  const [messages, setMessages] = useState([]);
  const [pinned, setPinned] = useState([]);
  const [text, setText] = useState("");
  const [replyTo, setReplyTo] = useState(null);
  const [sending, setSending] = useState(false);
  const [pickerFor, setPickerFor] = useState(null);
  const [notifOpen, setNotifOpen] = useState(false);
  const [notifItems, setNotifItems] = useState([]);
  const [highlightId, setHighlightId] = useState(null);
  const feedRef = useRef(null);
  const bottomRef = useRef(null);

  const isAdmin = access?.role === "admin";

  const load = useCallback(async () => {
    try {
      const r = await api.communityMessages();
      setMessages(r.messages || []);
      setPinned(r.pinned || []);
    } catch {
      /* ignore poll errors */
    }
  }, []);

  useEffect(() => {
    api.communityAccess().then(setAccess).catch(() => setAccess({ role: "locked", canRead: false }));
  }, []);

  // Clear reply/reaction notifications when the community is opened.
  useEffect(() => {
    if (access?.canRead) {
      api.communityNotificationsSeen().catch(() => {});
      window.dispatchEvent(new Event("community-seen"));
    }
  }, [access?.canRead]);

  const editName = async () => {
    const current = access?.displayName || "";
    const name = window.prompt("Nama tampilan Anda di komunitas (3–20 karakter):", current);
    if (name === null) return;
    try {
      const r = await api.communitySetProfile(name.trim());
      setAccess((a) => ({ ...a, displayName: r.displayName }));
      toast.success("Nama tampilan diperbarui.");
      load();
    } catch (e) {
      toast.error(e.message || "Nama tidak valid.");
    }
  };

  useEffect(() => {
    if (!access || !access.canRead) return;
    load();
    const t = setInterval(load, 4000);
    return () => clearInterval(t);
  }, [access, load]);

  useEffect(() => {
    bottomRef.current?.scrollIntoView({ behavior: "smooth" });
  }, [messages.length]);

  const send = async () => {
    const t = text.trim();
    if (!t) return;
    setSending(true);
    try {
      await api.communityPost(t, replyTo?.id || null);
      setText("");
      setReplyTo(null);
      await load();
    } catch (e) {
      toast.error(e.message || "Gagal mengirim pesan");
    } finally {
      setSending(false);
    }
  };

  const react = async (id, emoji) => {
    setPickerFor(null);
    try {
      await api.communityReact(id, emoji);
      await load();
    } catch (e) {
      toast.error(e.message || "Gagal");
    }
  };

  const openNotif = async () => {
    setNotifOpen((v) => !v);
    if (!notifOpen) {
      try {
        const r = await api.communityNotificationsList();
        setNotifItems(r.items || []);
      } catch { /* ignore */ }
    }
  };

  const goToMessage = (id) => {
    setNotifOpen(false);
    const el = document.querySelector(`[data-testid="community-msg-${id}"]`);
    if (el) {
      el.scrollIntoView({ behavior: "smooth", block: "center" });
      setHighlightId(id);
      setTimeout(() => setHighlightId(null), 2200);
    } else {
      toast("Pesan tidak lagi tersedia di daftar.");
    }
  };

  const report = async (id) => {
    const reason = window.prompt("Alasan melaporkan pesan ini? (opsional)") ?? null;
    if (reason === null) return;
    try {
      await api.communityReport(id, reason);
      toast.success("Terima kasih. Laporan Anda dikirim ke admin.");
    } catch (e) {
      toast.error(e.message || "Gagal melaporkan");
    }
  };

  const del = async (id) => {
    try { await api.communityDelete(id); await load(); } catch (e) { toast.error(e.message); }
  };
  const pin = async (id, currentlyPinned) => {
    let days = null;
    if (!currentlyPinned) {
      const ans = window.prompt(
        "Sematkan pengumuman berapa hari? Kosongkan untuk permanen (mis. 7):",
        ""
      );
      if (ans === null) return; // cancelled
      const n = parseInt(ans.trim(), 10);
      if (ans.trim() && (!Number.isFinite(n) || n <= 0)) {
        toast.error("Masukkan jumlah hari yang valid atau kosongkan.");
        return;
      }
      days = ans.trim() ? n : null;
    }
    try {
      await api.communityPin(id, days);
      toast.success(currentlyPinned ? "Sematan dilepas." : days ? `Disematkan ${days} hari.` : "Disematkan permanen.");
      await load();
    } catch (e) { toast.error(e.message); }
  };

  // ----- Locked (expired/free) state -----
  if (access && !access.canRead) {
    return (
      <div className="mx-auto min-h-screen w-full max-w-md lg:max-w-4xl px-6 pt-16 text-center" data-testid="community-locked">
        <div className="mx-auto flex h-14 w-14 items-center justify-center rounded-2xl bg-zinc-900">
          <Lock className="h-6 w-6 text-emerald-400" />
        </div>
        <h1 className="mt-5 font-heading text-xl font-extrabold text-zinc-900">Komunitas Premium</h1>
        <p className="mx-auto mt-2 max-w-xs text-sm leading-relaxed text-zinc-500">
          Komunitas ini merupakan bagian dari CeKKeU Premium. Aktifkan kembali Premium untuk
          bergabung dalam diskusi bersama member CeKKeU lainnya.
        </p>
        <button
          data-testid="community-reactivate-cta"
          onClick={() => navigate("/paywall?next=/app/komunitas")}
          className="mt-6 inline-flex h-12 items-center rounded-full bg-zinc-900 px-6 text-sm font-semibold text-white hover:bg-zinc-800"
        >
          Aktifkan Kembali Premium
        </button>
      </div>
    );
  }

  if (!access) {
    return <div className="px-6 pt-24 text-center text-sm text-zinc-400">Memuat…</div>;
  }

  return (
    <div className="mx-auto flex min-h-screen w-full max-w-md lg:max-w-4xl flex-col bg-white" data-testid="community-page">
      {/* Header */}
      <header className="sticky top-0 z-10 border-b border-zinc-100 bg-white/95 px-5 py-4 backdrop-blur">
        <div className="flex items-start gap-2">
          <Users className="mt-0.5 h-5 w-5 text-emerald-600" />
          <h1 className="flex-1 font-heading text-base font-extrabold text-zinc-900">Komunitas Premium CeKKeU</h1>
          <button
            data-testid="community-bell"
            onClick={openNotif}
            className="relative -mt-1 rounded-full p-2 text-zinc-500 hover:bg-zinc-100"
            aria-label="Notifikasi"
          >
            <Bell className="h-5 w-5" />
            {notifItems.some((n) => !n.seen) && !notifOpen && (
              <span className="absolute right-1.5 top-1.5 h-2 w-2 rounded-full bg-rose-500" />
            )}
          </button>
        </div>
        <p className="mt-0.5 text-xs text-zinc-500">
          Tempat berbagi pengalaman, tips, dan perjalanan menuju keuangan yang lebih sehat.
        </p>
        {access.canPost && !isAdmin && (
          <button
            data-testid="community-edit-name"
            onClick={editName}
            className="mt-2 inline-flex items-center gap-1.5 rounded-full bg-zinc-100 px-3 py-1 text-[11px] font-semibold text-zinc-600 hover:bg-zinc-200"
          >
            <Pencil className="h-3 w-3" /> {access.displayName}
          </button>
        )}

        {/* Notification panel */}
        {notifOpen && (
          <div className="absolute right-3 top-16 z-20 w-72 overflow-hidden rounded-2xl border border-zinc-200 bg-white shadow-xl" data-testid="notif-panel">
            <div className="flex items-center justify-between border-b border-zinc-100 px-3 py-2">
              <span className="text-xs font-bold text-zinc-700">Notifikasi</span>
              <button onClick={() => setNotifOpen(false)} className="text-zinc-400"><X className="h-4 w-4" /></button>
            </div>
            <div className="max-h-80 overflow-y-auto">
              {notifItems.length === 0 ? (
                <p className="px-3 py-6 text-center text-xs text-zinc-400">Belum ada notifikasi.</p>
              ) : (
                notifItems.map((n) => (
                  <button
                    key={n.id}
                    data-testid={`notif-item-${n.id}`}
                    onClick={() => goToMessage(n.messageId)}
                    className={`flex w-full items-start gap-2 border-b border-zinc-50 px-3 py-2.5 text-left hover:bg-zinc-50 ${!n.seen ? "bg-emerald-50/50" : ""}`}
                  >
                    <span className="text-base leading-none">{n.type === "react" ? (n.emoji || "❤️") : "💬"}</span>
                    <span className="min-w-0 flex-1 text-xs text-zinc-700">
                      <b>{n.fromName}</b> {n.type === "react" ? "bereaksi pada pesan Anda" : "membalas Anda"}
                      {n.text ? <span className="mt-0.5 block truncate text-[11px] text-zinc-400">“{n.text}”</span> : null}
                    </span>
                  </button>
                ))
              )}
            </div>
          </div>
        )}
      </header>

      {/* Pinned */}
      {pinned.length > 0 && (
        <div className="border-b border-amber-100 bg-amber-50 px-5 py-2.5" data-testid="community-pinned">
          {pinned.map((m) => (
            <div key={m.id} className="flex items-start gap-2 text-xs text-amber-900">
              <Pin className="mt-0.5 h-3.5 w-3.5 shrink-0 text-amber-600" />
              <span>
                <b>{m.displayName}:</b> {m.text}
                {m.pinnedUntil && (
                  <span className="ml-1 text-amber-600">· berakhir {timeLabel(m.pinnedUntil)}</span>
                )}
              </span>
            </div>
          ))}
        </div>
      )}

      {/* Feed */}
      <div ref={feedRef} className="flex-1 space-y-3 overflow-y-auto px-4 py-4" data-testid="community-feed">
        {messages.length === 0 && (
          <p className="pt-10 text-center text-sm text-zinc-400">Belum ada pesan. Jadilah yang pertama menyapa 👋</p>
        )}
        {messages.map((m) => (
          <div key={m.id} data-testid={`community-msg-${m.id}`} className={`rounded-2xl border p-3 transition-shadow ${m.isAdmin ? "border-emerald-200 bg-emerald-50" : "border-zinc-100 bg-zinc-50/70"} ${highlightId === m.id ? "ring-2 ring-emerald-400 ring-offset-1" : ""}`}>
            <div className="flex items-center gap-1.5">
              <span className={`text-xs font-bold ${m.isAdmin ? "text-emerald-800" : "text-zinc-800"}`}>{m.displayName}</span>
              {m.isAdmin && (
                <span className="inline-flex items-center gap-0.5 rounded-full bg-emerald-600 px-1.5 py-0.5 text-[9px] font-bold uppercase text-white">
                  <ShieldCheck className="h-2.5 w-2.5" /> Admin
                </span>
              )}
              <span className="ml-auto text-[10px] text-zinc-400">{timeLabel(m.createdAt)}</span>
            </div>
            {m.replyTo && (
              <div className="mt-1.5 rounded-lg border-l-2 border-zinc-300 bg-white px-2 py-1 text-[11px] text-zinc-500">
                <b>{m.replyTo.displayName}:</b> {m.replyTo.text}
              </div>
            )}
            {m.deleted ? (
              <p className="mt-1 text-sm italic text-zinc-400">Pesan ini telah dihapus.</p>
            ) : (
              <p className="mt-1 whitespace-pre-wrap break-words text-sm text-zinc-800">{m.text}</p>
            )}

            {!m.deleted && (
              <div className="mt-2 flex flex-wrap items-center gap-1.5">
                {Object.entries(m.reactions || {}).map(([e, c]) => (
                  <span key={e} className={`rounded-full border px-2 py-0.5 text-xs ${m.myReactions?.includes(e) ? "border-emerald-300 bg-emerald-100" : "border-zinc-200 bg-white"}`}>
                    {e} {c}
                  </span>
                ))}
                {access.canPost && (
                  <div className="ml-auto flex items-center gap-1">
                    {REACTIONS.map((e) => (
                      <button key={e} data-testid={`react-${m.id}-${e}`} onClick={() => react(m.id, e)} className="rounded-full px-1 text-sm hover:bg-zinc-200">{e}</button>
                    ))}
                    <button data-testid={`emoji-more-${m.id}`} onClick={() => setPickerFor(pickerFor === m.id ? null : m.id)} className="rounded-full p-1 text-zinc-400 hover:text-emerald-600"><SmilePlus className="h-3.5 w-3.5" /></button>
                    <button data-testid={`reply-${m.id}`} onClick={() => setReplyTo({ id: m.id, displayName: m.displayName })} className="rounded-full p-1 text-zinc-400 hover:text-zinc-700"><Reply className="h-3.5 w-3.5" /></button>
                    <button data-testid={`report-${m.id}`} onClick={() => report(m.id)} className="rounded-full p-1 text-zinc-400 hover:text-rose-600"><Flag className="h-3.5 w-3.5" /></button>
                    {isAdmin && (
                      <>
                        <button data-testid={`pin-${m.id}`} onClick={() => pin(m.id, m.pinned)} className={`rounded-full p-1 ${m.pinned ? "text-amber-600" : "text-zinc-400 hover:text-amber-600"}`}><Pin className="h-3.5 w-3.5" /></button>
                        <button data-testid={`del-${m.id}`} onClick={() => del(m.id)} className="rounded-full p-1 text-zinc-400 hover:text-rose-600"><Trash2 className="h-3.5 w-3.5" /></button>
                      </>
                    )}
                  </div>
                )}
              </div>
            )}

            {pickerFor === m.id && (
              <div className="mt-2 flex flex-wrap gap-1 rounded-xl border border-zinc-200 bg-white p-2" data-testid={`emoji-picker-${m.id}`}>
                {MORE_EMOJIS.map((e) => (
                  <button key={e} data-testid={`picker-${m.id}-${e}`} onClick={() => react(m.id, e)} className="rounded-lg px-1.5 py-1 text-lg hover:bg-zinc-100">{e}</button>
                ))}
              </div>
            )}
          </div>
        ))}
        <div ref={bottomRef} />
      </div>

      {/* Composer */}
      {access.canPost ? (
        <div className="sticky bottom-0 border-t border-zinc-100 bg-white px-4 pb-4 pt-2">
          <div className="mb-1.5 flex items-start gap-1.5 text-[10px] leading-tight text-zinc-400" data-testid="community-privacy-note">
            <AlertTriangle className="mt-0.5 h-3 w-3 shrink-0" />
            Jangan membagikan informasi sensitif seperti nomor rekening, PIN, password, OTP, atau data keuangan pribadi.
          </div>
          {replyTo && (
            <div className="mb-1.5 flex items-center gap-2 rounded-lg bg-zinc-100 px-2 py-1 text-[11px] text-zinc-600" data-testid="community-reply-preview">
              Membalas <b>{replyTo.displayName}</b>
              <button onClick={() => setReplyTo(null)} className="ml-auto text-zinc-400"><X className="h-3.5 w-3.5" /></button>
            </div>
          )}
          <div className="flex items-end gap-2">
            <textarea
              data-testid="community-input"
              value={text}
              onChange={(e) => setText(e.target.value)}
              rows={1}
              placeholder="Tulis pesan…"
              className="max-h-28 flex-1 resize-none rounded-2xl border border-zinc-200 px-4 py-2.5 text-sm outline-none focus:border-emerald-400 focus:ring-2 focus:ring-emerald-100"
              onKeyDown={(e) => { if (e.key === "Enter" && !e.shiftKey) { e.preventDefault(); send(); } }}
            />
            <button
              data-testid="community-send"
              onClick={send}
              disabled={sending || !text.trim()}
              className="flex h-11 w-11 shrink-0 items-center justify-center rounded-full bg-zinc-900 text-white disabled:opacity-40"
            >
              <Send className="h-5 w-5" />
            </button>
          </div>
        </div>
      ) : (
        <div className="border-t border-zinc-100 bg-zinc-50 px-5 py-3 text-center text-xs text-zinc-500" data-testid="community-post-blocked">
          Anda tidak dapat mengirim pesan di komunitas saat ini.
        </div>
      )}
    </div>
  );
}
